import { tenantThemeVariables } from '../utils/tenantTheme';
import { tenantsAPI } from '../utils/api';
import { tenantLogoSrc } from './TenantLogoField';
import { managementPermissions } from '../config/managementPermissions';
import { useBridgeAccess } from '../hooks/useBridgeAccess';
import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { EnvironmentBanner } from './EnvironmentBanner';
import {
  HomeIcon,
  DocumentTextIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  ClipboardDocumentListIcon,
  BuildingOffice2Icon,
  RectangleStackIcon,
  UserGroupIcon,
  UserPlusIcon,
  UsersIcon,
  CubeIcon,
  DocumentDuplicateIcon,
  WrenchScrewdriverIcon,
  ChevronDownIcon,
  ShieldCheckIcon,
  TagIcon,
  GiftIcon,
  ReceiptPercentIcon,
  ShoppingCartIcon,
  UserCircleIcon,
  ChatBubbleLeftRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  DevicePhoneMobileIcon,
  InboxIcon,
  ArrowPathIcon,
  CircleStackIcon,
  BookmarkIcon,
  SparklesIcon,
  MegaphoneIcon,
  IdentificationIcon,
  CalendarDaysIcon,
  SwatchIcon,
  SignalIcon,
  Square3Stack3DIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  CheckBadgeIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { useCartStore } from '../stores/cartStore';
import { offersAPI } from '../utils/api';
import { OffertifyLogo } from './OffertifyLogo';
import ChatWidget from './chat/ChatWidget';
import { usePermissions } from '../hooks/usePermissions';

type NavItem = {
  name: string;
  href: string;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  permission?: string;
  anyOf?: string[];
  adminOnly?: boolean;
  badge?: number;
};

const Layout: React.FC = () => {
  const { user, tenant, logout, stopImpersonation } = useAuth();
  const { data: branding } = useQuery({
    queryKey: ['tenant-branding', tenant?.id],
    queryFn: async () => (await tenantsAPI.getMe()).data,
    enabled: !!tenant && !user?.is_host,
  });
  const logo = tenantLogoSrc(branding ? branding.logo_url : tenant?.logo);
  const showTenantName = !logo || branding?.mostra_nome_tenant !== false;
  useEffect(() => {
    const variables = tenantThemeVariables(user?.is_host ? undefined : branding);
    for (const [key, value] of Object.entries(variables)) document.documentElement.style.setProperty(key, value);
    return () => { for (const key of Object.keys(variables)) document.documentElement.style.removeProperty(key); };
  }, [branding?.colore_primario, branding?.colore_secondario, user?.is_host]);
  const whatsappAccess = useBridgeAccess();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar-collapsed') === 'true';
    } catch {
      return false;
    }
  });

  // Close the mobile drawer whenever the route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Persist the desktop collapsed preference
  useEffect(() => {
    try {
      localStorage.setItem('sidebar-collapsed', String(collapsed));
    } catch {
      /* ignore storage errors */
    }
  }, [collapsed]);

  const cartItemCount = useCartStore((s) => s.getItemCount());

  // Conteggio richieste sconto in attesa (badge menu, solo per chi approva)
  const canApproveDiscount = hasPermission('offerte:approve_discount');
  const { data: pendingApprovalsData } = useQuery({
    queryKey: ['pending-approvals-count'],
    queryFn: () => offersAPI.getPendingApproval({ page: 1, limit: 1 }),
    refetchInterval: 60000,
    enabled: canApproveDiscount && !user?.is_host && user?.role !== 'cliente',
  });
  const pendingApprovalsCount = pendingApprovalsData?.data?.pagination?.total || 0;

  // Different navigation for host users vs tenant users vs cliente
  const navigation = user?.is_host
    ? {
        main: [
          { name: 'Dashboard', href: '/', icon: HomeIcon },
          { name: 'Tenant', href: '/tenants', icon: BuildingOffice2Icon },
          { name: 'Piani', href: '/piani', icon: RectangleStackIcon },
          { name: 'Sync Data', href: '/sync-admin', icon: ArrowPathIcon },
          { name: 'Sync Query Explorer', href: '/sync-query-explorer', icon: CircleStackIcon },
          { name: 'Anagrafica Imprese', href: '/host/openapi', icon: IdentificationIcon },
          { name: 'Telegram Bot', href: '/telegram-settings', icon: ChatBubbleLeftRightIcon },
          { name: 'Messaggi', href: '/messages', icon: InboxIcon, adminOnly: true },
          { name: 'Conversazioni', href: '/conversations', icon: ChatBubbleOvalLeftEllipsisIcon, adminOnly: true },
        ] as NavItem[],
        admin: [
          { name: 'Utenti', href: '/users', icon: UsersIcon },
          { name: 'Ruoli e Permessi', href: '/roles', icon: ShieldCheckIcon },
          { name: 'Registro Attività', href: '/audit', icon: ClipboardDocumentListIcon },
          { name: 'Impostazioni', href: '/settings', icon: CogIcon },
        ] as NavItem[],
      }
    : user?.role === 'cliente'
    ? {
        main: [
          { name: 'Catalogo', href: '/catalog', icon: CubeIcon },
          { name: 'I Miei Ordini', href: '/my-orders', icon: DocumentTextIcon },
        ] as NavItem[],
        admin: [] as NavItem[],
      }
    : {
        main: [
          { name: 'Dashboard', href: '/', icon: HomeIcon },
          { name: 'Offerte', href: '/sales-documents?type=offerta', icon: DocumentTextIcon, permission: 'offerte:view' },
          { name: 'Ordini', href: '/sales-documents?type=ordine', icon: DocumentDuplicateIcon, permission: 'offerte:view' },
          { name: 'DDT', href: '/sales-documents?type=ddt', icon: DocumentDuplicateIcon, permission: 'offerte:view' },
          { name: 'Fatture', href: '/sales-documents?type=fattura', icon: DocumentDuplicateIcon, permission: 'offerte:view' },
          { name: 'Approvazioni', href: '/discount-approvals', icon: CheckBadgeIcon, permission: 'offerte:approve_discount', badge: pendingApprovalsCount },
          { name: 'Lead', href: '/leads', icon: UserPlusIcon, permission: 'leads:view' },
          { name: 'Clienti', href: '/clients', icon: UserGroupIcon, permission: 'clienti:view' },
          { name: 'Attività', href: '/attivita', icon: CalendarDaysIcon, permission: 'visite:view' },
          { name: 'Prodotti', href: '/products', icon: CubeIcon, permission: 'prodotti:view' },
          { name: 'Cataloghi', href: '/cataloghi', icon: CubeIcon, permission: 'cataloghi:view' },
          { name: 'Listini', href: '/price-lists', icon: TagIcon, permission: 'listini:view' },
          { name: 'Promozioni', href: '/promotions', icon: GiftIcon, permission: 'promozioni:view' },
          { name: 'Template', href: '/templates', icon: DocumentDuplicateIcon, anyOf: managementPermissions['template'] },
          { name: 'Tipologie Attività', href: '/activity-types', icon: SwatchIcon, anyOf: managementPermissions['activity_types'] },
          { name: 'Canali', href: '/canali', icon: SignalIcon, anyOf: managementPermissions['canali'] },
          { name: 'Settori Merceologici', href: '/settori-merceologici', icon: Square3Stack3DIcon, anyOf: managementPermissions['settori'] },
          { name: 'Form pubblici Lead', href: '/leads/intake-forms', icon: InboxIcon, anyOf: managementPermissions['leads:config'] },
          { name: 'Tag Lead', href: '/leads/tags', icon: BookmarkIcon, anyOf: managementPermissions['leads:config'] },
          { name: 'Regole Scoring Lead', href: '/leads/scoring-rules', icon: SparklesIcon, anyOf: managementPermissions['leads:config'] },
          { name: 'Segmenti Lead', href: '/leads/segments', icon: RectangleStackIcon, anyOf: managementPermissions['leads:config'] },
          { name: 'Campagne Lead', href: '/leads/campaigns', icon: MegaphoneIcon, anyOf: managementPermissions['leads:config'] },
          { name: 'Autorizzazioni Sconto', href: '/discount-authorizations', icon: ReceiptPercentIcon, anyOf: managementPermissions['autorizzazioni'] },
          { name: 'Template Email', href: '/email-templates', icon: EnvelopeIcon, anyOf: managementPermissions['email_templates'] },
        ] as NavItem[],
        admin: [
          { name: 'Sync Data', href: '/sync-admin', icon: ArrowPathIcon, permission: 'sync:manage' },
          { name: 'WhatsApp', href: '/whatsapp-settings', icon: DevicePhoneMobileIcon, permission: 'settings:integrations' },
          { name: 'Messaggi', href: '/messages', icon: InboxIcon, adminOnly: true },
          { name: 'Conversazioni', href: '/conversations', icon: ChatBubbleOvalLeftEllipsisIcon, adminOnly: true },
          { name: 'Utenti', href: '/users', icon: UsersIcon, permission: 'users:view' },
          { name: 'Ruoli e Permessi', href: '/roles', icon: ShieldCheckIcon, permission: 'users:view' },
          { name: 'Registro Attività', href: '/audit', icon: ClipboardDocumentListIcon, permission: 'audit:view' },
          { name: 'Impostazioni', href: '/settings', icon: CogIcon, permission: 'settings:view' },
        ] as NavItem[],
      };

  if (whatsappAccess.data?.enabled && !user?.is_host) navigation.main.push({ name: 'Chat WhatsApp', href: '/whatsapp', icon: ChatBubbleOvalLeftEllipsisIcon });
  const visibleMainNav = navigation.main.filter((item) =>
    (!item.permission || hasPermission(item.permission)) &&
    (!item.anyOf || hasAnyPermission(item.anyOf))
  );
  const visibleAdminNav = navigation.admin.filter((item) =>
    (user?.is_host || user?.role === 'admin') &&
    (!item.adminOnly || user?.is_host || user?.role === 'admin') &&
    (!item.permission || hasPermission(item.permission))
  );

  const allVisibleItems = [...visibleMainNav, ...visibleAdminNav];
  const activeHref = allVisibleItems
    .filter(item => {
      if (item.href.includes('?type=')) {
        const type = item.href.split('type=')[1];
        if (location.pathname.startsWith('/offers')) return type === 'offerta';
        const currentType = new URLSearchParams(location.search).get('type') || (new URLSearchParams(location.search).has('offerta') ? 'offerta' : 'ordine');
        return location.pathname.startsWith('/sales-documents') && currentType === type;
      }
      return item.href === '/' ? location.pathname === '/' : location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
    })
    .sort((a, b) => b.href.length - a.href.length)[0]?.href;
  const isActive = (path: string) => path === activeHref;
  const groupDefinitions = user?.is_host ? [
    { name: 'Piattaforma', icon: BuildingOffice2Icon, paths: ['/tenants', '/piani', '/host/openapi'] },
    { name: 'Dati e integrazioni', icon: ArrowPathIcon, paths: ['/sync-admin', '/sync-query-explorer', '/telegram-settings'] },
    { name: 'Comunicazioni', icon: ChatBubbleLeftRightIcon, paths: ['/messages', '/conversations'] },
    { name: 'Amministrazione', icon: WrenchScrewdriverIcon, paths: ['/users', '/roles', '/audit', '/settings'] },
  ] : [
    { name: 'Vendite', icon: DocumentTextIcon, paths: ['/sales-documents?type=offerta', '/sales-documents?type=ordine', '/sales-documents?type=ddt', '/sales-documents?type=fattura', '/discount-approvals', '/clients'] },
    { name: 'Lead e marketing', icon: UserPlusIcon, paths: ['/leads', '/leads/campaigns', '/leads/segments'] },
    { name: 'Catalogo e prezzi', icon: CubeIcon, paths: ['/products', '/cataloghi', '/price-lists', '/promotions'] },
    { name: 'Comunicazioni', icon: ChatBubbleLeftRightIcon, paths: ['/whatsapp', '/messages', '/conversations'] },
    { name: 'Configurazione', icon: CogIcon, paths: ['/templates', '/email-templates', '/discount-authorizations', '/activity-types', '/canali', '/settori-merceologici', '/leads/intake-forms', '/leads/tags', '/leads/scoring-rules'] },
    { name: 'Amministrazione', icon: WrenchScrewdriverIcon, paths: ['/users', '/roles', '/sync-admin', '/whatsapp-settings', '/audit', '/settings'] },
  ];
  const navGroups = groupDefinitions.map(group => ({ ...group,
    items: group.paths.flatMap(path => allVisibleItems.filter(item => item.href === path)),
  })).filter(group => group.items.length > 0);
  const activeGroupName = navGroups.find(group => group.items.some(item => isActive(item.href)))?.name;
  useEffect(() => {
    if (activeGroupName) setExpandedGroups(current => ({ ...current, [activeGroupName]: true }));
  }, [activeGroupName, location.pathname]);
  const navLabel = (item: NavItem) => item.href === '/templates' ? 'Template offerte' : item.href === '/sync-admin' ? 'Sincronizzazione dati' : item.name;

  // Base classes for a nav link. When the sidebar is collapsed on desktop the
  // icons are centered and labels are hidden (labels always show on the mobile drawer).
  const navItemClass = (path: string, indent = false) => `
    flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
    ${indent ? (collapsed ? 'pl-6 md:pl-2' : 'pl-6') : ''}
    ${collapsed ? 'md:justify-center md:space-x-0 md:px-2' : ''}
    ${isActive(path)
      ? 'bg-primary-100 text-primary-700'
      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }
  `;
  // Hide a label on desktop when collapsed; it stays visible on the mobile drawer.
  const labelClass = collapsed ? 'md:hidden' : '';

  return (
    <div className="fixed inset-0 flex min-h-0 overflow-hidden bg-gray-100">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 z-40 flex flex-col w-64 bg-white shadow-lg
          transform transition-all duration-200 ease-in-out
          md:static md:z-auto md:translate-x-0
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
          ${collapsed ? 'md:w-20' : 'md:w-64'}
        `}
      >
        <div className={`relative flex items-center justify-center h-16 px-4 ${user?.is_host ? 'bg-purple-600' : 'bg-primary-600'}`}>
          <div className="flex flex-col items-center w-full">
            <div className="flex items-center space-x-2">
              {logo ? (
                <img src={logo} alt={tenant?.name || "Logo tenant"} className={`h-8 w-auto object-contain rounded flex-shrink-0 ${collapsed ? 'max-w-10' : showTenantName ? 'max-w-24' : 'max-w-48'}`} />
              ) : (
                <OffertifyLogo className="h-8 w-8 text-white flex-shrink-0" />
              )}
              {showTenantName && <span className={`text-white font-semibold text-lg truncate ${labelClass}`}>
                {tenant?.name || 'Offertify'}
              </span>}
            </div>
            {user?.is_host && (
              <span className={`text-xs text-purple-200 mt-1 ${labelClass}`}>
                Amministratore Piattaforma
              </span>
            )}
          </div>
          {/* Close button (mobile drawer only) */}
          <button
            onClick={() => setMobileOpen(false)}
            className="absolute right-3 text-white md:hidden"
            aria-label="Chiudi menu"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
          {/* Main navigation items */}
          {(user?.role === 'cliente' ? visibleMainNav : visibleMainNav.filter(item => item.href === '/' || item.href === '/attivita')).map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                title={collapsed ? item.name : undefined}
                className={navItemClass(item.href)}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                <span className={labelClass}>{item.name}</span>
                {(item.badge ?? 0) > 0 && (
                  <span className={`ml-auto inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-primary-600 rounded-full ${collapsed ? 'md:hidden' : ''}`}>
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}

          {/* Cliente extra nav: Cart + Profile */}
          {user?.role === 'cliente' && (
            <div className="pt-2 space-y-1">
              <Link
                to="/cart"
                title={collapsed ? 'Carrello' : undefined}
                className={navItemClass('/cart')}
              >
                <ShoppingCartIcon className="h-5 w-5 flex-shrink-0" />
                <span className={labelClass}>Carrello</span>
                {cartItemCount > 0 && (
                  <span className={`ml-auto inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-primary-600 rounded-full ${collapsed ? 'md:hidden' : ''}`}>
                    {cartItemCount}
                  </span>
                )}
              </Link>
              <Link
                to="/profile"
                title={collapsed ? 'Profilo' : undefined}
                className={navItemClass('/profile')}
              >
                <UserCircleIcon className="h-5 w-5 flex-shrink-0" />
                <span className={labelClass}>Profilo</span>
              </Link>
            </div>
          )}

          {user?.role !== 'cliente' && navGroups.map((group, groupIndex) => {
            const GroupIcon = group.icon;
            const expanded = expandedGroups[group.name] ?? false;
            const active = group.name === activeGroupName;
            const badge = group.items.reduce((sum, item) => sum + (item.badge || 0), 0);
            const panelId = `navigation-group-${groupIndex}`;
            return <div key={group.name}>
              <button type="button" aria-expanded={expanded} aria-controls={panelId}
                title={collapsed ? group.name : undefined}
                onClick={() => {
                  if (collapsed) { setCollapsed(false); setExpandedGroups(current => ({ ...current, [group.name]: true })); }
                  else setExpandedGroups(current => ({ ...current, [group.name]: !expanded }));
                }}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium hover:bg-gray-100 ${active ? 'text-primary-700' : 'text-gray-700'} ${collapsed ? 'md:justify-center md:px-2' : ''}`}>
                <GroupIcon className="h-5 w-5 shrink-0" />
                <span className={`flex-1 ${labelClass}`}>{group.name}</span>
                {badge > 0 && <span className={`rounded-full bg-primary-600 px-1.5 text-xs text-white ${labelClass}`}>{badge}</span>}
                <ChevronDownIcon className={`h-4 w-4 shrink-0 transition-transform ${expanded ? '' : '-rotate-90'} ${labelClass}`} />
              </button>
              <div id={panelId} hidden={!expanded} className={`ml-5 mt-1 space-y-1 border-l border-gray-200 pl-2 ${collapsed ? 'md:hidden' : ''}`}>
                {group.items.map(item => <Link key={item.href} to={item.href} aria-current={isActive(item.href) ? 'page' : undefined}
                  className={`flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm ${isActive(item.href) ? 'bg-primary-100 font-medium text-primary-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}>
                  <span>{navLabel(item)}</span>
                  {(item.badge || 0) > 0 && <span className="rounded-full bg-primary-600 px-1.5 text-xs text-white">{item.badge}</span>}
                </Link>)}
              </div>
            </div>;
          })}
        </nav>

        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => navigate(user?.role === 'cliente' ? '/profile' : '/my-profile')}
            title={collapsed ? user?.name : undefined}
            className={`flex items-center space-x-3 mb-3 w-full text-left rounded-lg px-1 py-1 -mx-1 hover:bg-gray-100 transition-colors ${collapsed ? 'md:justify-center md:space-x-0' : ''}`}
          >
            {user?.avatar ? (
              <img src={user.avatar} alt={user.name} className="h-8 w-8 rounded-full flex-shrink-0" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-medium text-gray-600">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div className={`flex-1 min-w-0 ${labelClass}`}>
              <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </button>

          <button
            onClick={logout}
            title={collapsed ? 'Logout' : undefined}
            className={`flex items-center space-x-2 w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors ${collapsed ? 'md:justify-center md:space-x-0' : ''}`}
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4 flex-shrink-0" />
            <span className={labelClass}>Logout</span>
          </button>

          {/* Desktop collapse toggle */}
          <button
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? 'Espandi menu' : 'Comprimi menu'}
            className={`hidden md:flex items-center space-x-2 w-full mt-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors ${collapsed ? 'md:justify-center md:space-x-0' : ''}`}
          >
            {collapsed ? (
              <ChevronDoubleRightIcon className="h-4 w-4 flex-shrink-0" />
            ) : (
              <ChevronDoubleLeftIcon className="h-4 w-4 flex-shrink-0" />
            )}
            <span className={labelClass}>Comprimi</span>
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Environment banner (non-production only) */}
        <EnvironmentBanner />

        {/* Mobile top bar with hamburger */}
        <div className="flex items-center h-14 px-4 bg-white shadow-sm md:hidden">
          <button
            onClick={() => setMobileOpen(true)}
            className="text-gray-600 hover:text-gray-900"
            aria-label="Apri menu"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex items-center space-x-2 ml-3">
            {logo ? (
              <img src={logo} alt={tenant?.name || "Logo tenant"} className={`h-7 w-auto object-contain rounded ${showTenantName ? 'max-w-24' : 'max-w-48'}`} />
            ) : (
              <OffertifyLogo className={`h-7 w-7 ${user?.is_host ? 'text-purple-600' : 'text-primary-600'}`} />
            )}
            {showTenantName && <span className="font-semibold text-gray-900 truncate">
              {tenant?.name || 'Offertify'}
            </span>}
          </div>
        </div>

        {/* Impersonation banner */}
        {user?.impersonating && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    Stai impersonando: <span className="font-semibold">{user?.name}</span> in <span className="font-semibold">{tenant?.name}</span>
                  </p>
                </div>
              </div>
              <button
                onClick={stopImpersonation}
                className="inline-flex items-center px-3 py-1.5 border border-yellow-300 text-sm font-medium rounded-md text-yellow-700 bg-white hover:bg-yellow-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                Torna alla mia sessione
              </button>
            </div>
          </div>
        )}

        <main className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="p-6">
            <Outlet />
          </div>
        </main>
      </div>

      {user?.role !== 'cliente' && <ChatWidget />}
    </div>
  );
};

export default Layout;
